import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useErrorToast, useDismissError } from './ErrorToastContext';
import { formatDate } from './tenantClock';
import RichText from './RichText';
import CancelButton from './CancelButton';

// Email Activities logged by Email Sync, as review cards (issue #259): My
// Account's Sync Now panel and the CRM Dashboard's Synced Email card. Each
// row is the backend's syncedEmailService shape. Clicking a card opens the
// record it was logged against. A card with can_remove (logged from the
// viewer's own mailbox) has Remove..., which deletes it, optionally adding
// one of its addresses or domains to the viewer's never-log list.
// onRemoved(activity, accountData, message) runs after a removal.
// showStaff names whose mailbox each one came from. compact (the CRM
// Dashboard) shows each as a one-line row that expands in place, one at a
// time, to its full detail and body (ui-architecture.md's Inline Row
// Expand), instead of a full card per email.

const LINK_BUTTON = { background: 'none', border: 'none', padding: 0, color: '#1d4ed8', textDecoration: 'underline', cursor: 'pointer', fontSize: 12 };
const CHOICE_BUTTON = { padding: '5px 10px', borderRadius: 6, border: '1px solid #bfc8d6', background: '#fff', color: '#b91c1c', fontWeight: 500, fontSize: 12, cursor: 'pointer', textAlign: 'left' };

// Never-log choices for one synced email: each other-party address, then
// each of their domains.
function neverLogChoices(activity) {
  const addresses = activity.other_party || [];
  const domains = [...new Set(addresses.map((a) => a.split('@')[1]).filter(Boolean))];
  return [...addresses, ...domains];
}

// Inline rather than a dropdown so a scrolling list never clips it.
function RemoveChoices({ activity, onRemoved, onCancel }) {
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [busy, setBusy] = useState(false);

  const remove = async (neverLog) => {
    setBusy(true);
    try {
      const res = await axios.delete(`/api/my-account/logged-emails/${activity.id}`, { data: neverLog ? { never_log: neverLog } : {} });
      dismissError();
      onRemoved(activity, res.data.data, res.data.message);
    } catch (err) {
      showError(err, 'Could not remove that email');
      setBusy(false);
    }
  };

  return (
    <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #e3e7ee', display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' }}>
      <button type="button" disabled={busy} onClick={() => remove(null)} title="Delete this email Activity" style={CHOICE_BUTTON}>
        Remove
      </button>
      {neverLogChoices(activity).map((value) => (
        <button
          key={value}
          type="button"
          disabled={busy}
          onClick={() => remove(value)}
          title={`Delete this email Activity and add ${value} to your never-log list`}
          style={{ ...CHOICE_BUTTON, overflowWrap: 'anywhere' }}
        >
          Remove and never log {value}
        </button>
      ))}
      <CancelButton disabled={busy} onClick={onCancel} title="Keep this email" style={{ padding: '2px 10px', fontSize: 12, marginLeft: 6 }} />
    </div>
  );
}

function whenLabel(a) {
  return `${formatDate(a.activity_date)}${a.start_time ? ` ${String(a.start_time).slice(0, 5)}` : ''}`;
}

function directionStyle(a) {
  if (a.activity_type === 'meeting') return { fontWeight: 600, color: '#1976d2' };
  return { fontWeight: 600, color: a.direction === 'outbound' ? '#1d4ed8' : '#2e7d32' };
}

// Sent / Received for an email, Outbound / Inbound for a Call, Meeting for
// a Meeting (no direction).
function directionLabel(a) {
  if (a.activity_type === 'meeting') return 'Meeting';
  if (a.activity_type === 'call') return a.direction === 'outbound' ? 'Outbound' : 'Inbound';
  return a.direction === 'outbound' ? 'Sent' : 'Received';
}

function isEmail(a) {
  return !a.activity_type || a.activity_type === 'email';
}

// Remove... and its inline choices, for an email the viewer can remove.
function RemoveControl({ activity, open, onOpen, onClose, onRemoved }) {
  if (open) {
    return <RemoveChoices activity={activity} onRemoved={(...args) => { onClose(); onRemoved(...args); }} onCancel={onClose} />;
  }
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onOpen(); }}
      title="Show options to remove this email"
      style={{ ...LINK_BUTTON, color: '#b91c1c' }}
    >
      Remove...
    </button>
  );
}

// The expanded detail of one compact row: addresses, record, mailbox and
// the email body, fetched on first expand.
function ExpandedEmail({ activity: a, showStaff, removing, setRemoving, onRemoved }) {
  const navigate = useNavigate();
  const [body, setBody] = useState(null);
  const [bodyError, setBodyError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`/api/activities/synced-emails/${a.id}`)
      .then((res) => { if (!cancelled) setBody(res.data.data?.notes || ''); })
      .catch(() => { if (!cancelled) setBodyError(true); });
    return () => { cancelled = true; };
  }, [a.id]);

  const labelStyle = { color: '#5a6b85' };
  let bodyContent;
  if (bodyError) bodyContent = <span style={{ fontSize: 13, color: '#b91c1c' }}>Could not load the email body.</span>;
  else if (body === null) bodyContent = <span style={{ fontSize: 13, color: '#888' }}>Loading...</span>;
  else if (body) bodyContent = <RichText html={body} style={{ fontSize: 13, color: '#444' }} />;
  else bodyContent = <span style={{ fontSize: 13, color: '#888' }}>{isEmail(a) ? '(no body)' : '(no notes)'}</span>;

  return (
    <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #e3e7ee', cursor: 'default' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr', rowGap: 4, fontSize: 13 }}>
        {isEmail(a) && (
          <>
            <span style={labelStyle}>From</span>
            <span style={{ overflowWrap: 'anywhere' }}>{a.email_from || '—'}</span>
            <span style={labelStyle}>To</span>
            <span style={{ overflowWrap: 'anywhere' }}>{a.email_to || '—'}</span>
          </>
        )}
        <span style={labelStyle}>Record</span>
        <span>
          {a.commercial_entity?.name || '—'}
          {a.also_on_count > 0 && <span style={{ color: '#888' }}> · also on {a.also_on_count} other record{a.also_on_count === 1 ? '' : 's'}</span>}
        </span>
        {showStaff && (
          <>
            <span style={labelStyle}>{isEmail(a) ? 'Mailbox' : 'Logged by'}</span>
            <span>{isEmail(a) ? (a.staff_name ? `${a.staff_name}'s mailbox` : 'Shared mailbox') : (a.staff_name || '—')}</span>
          </>
        )}
      </div>
      <div style={{ marginTop: 10, maxHeight: 320, overflowY: 'auto', background: '#f6f8fa', border: '1px solid #e3e7ee', borderRadius: 6, padding: '8px 10px' }}>
        {bodyContent}
      </div>
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', marginTop: 10, flexWrap: 'wrap' }}>
        {a.commercial_entity && (
          <button
            type="button"
            onClick={() => navigate(`/commercial-entities/${a.commercial_entity.id}`)}
            title={`Open ${a.commercial_entity.name}`}
            style={LINK_BUTTON}
          >
            Open record
          </button>
        )}
        {a.can_remove && onRemoved && (
          <RemoveControl activity={a} open={removing} onOpen={() => setRemoving(true)} onClose={() => setRemoving(false)} onRemoved={onRemoved} />
        )}
      </div>
    </div>
  );
}

function CompactList({ activities, onRemoved, showStaff }) {
  const [expandedId, setExpandedId] = useState(null);
  const [removingId, setRemovingId] = useState(null);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {activities.map((a) => {
        const expanded = expandedId === a.id;
        return (
          <div
            key={a.id}
            onClick={() => { setExpandedId(expanded ? null : a.id); setRemovingId(null); }}
            title={expanded ? 'Collapse' : (isEmail(a) ? 'Show this email' : 'Show its notes')}
            style={{ padding: '6px 10px', background: expanded ? '#fff' : '#fbfcfd', border: `1px solid ${expanded ? '#bfc8d6' : '#e3e7ee'}`, borderRadius: 6, cursor: 'pointer' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, minWidth: 0 }}>
              <span style={{ ...directionStyle(a), fontSize: 12, width: 62, flexShrink: 0 }}>{directionLabel(a)}</span>
              <span style={{ flex: 2, minWidth: 0, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: expanded ? 'normal' : 'nowrap', overflowWrap: 'anywhere' }}>
                {a.topic || '(no subject)'}
              </span>
              <span style={{ flex: 1, minWidth: 0, color: '#555', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {a.commercial_entity?.name || '—'}
              </span>
              <span style={{ flexShrink: 0, fontSize: 12, color: '#888' }}>{whenLabel(a)}</span>
            </div>
            {expanded && (
              <ExpandedEmail
                activity={a}
                showStaff={showStaff}
                removing={removingId === a.id}
                setRemoving={(on) => setRemovingId(on ? a.id : null)}
                onRemoved={onRemoved}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function SyncedEmailList({ activities, onRemoved, showStaff = false, compact = false }) {
  const navigate = useNavigate();
  const [removingId, setRemovingId] = useState(null);

  if (compact) return <CompactList activities={activities} onRemoved={onRemoved} showStaff={showStaff} />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {activities.map((a) => (
        <div
          key={a.id}
          onClick={() => a.commercial_entity && navigate(`/commercial-entities/${a.commercial_entity.id}`)}
          title={a.commercial_entity ? `Open ${a.commercial_entity.name}` : undefined}
          style={{ padding: '10px 12px', background: '#fff', border: '1px solid #e3e7ee', borderRadius: 6, cursor: a.commercial_entity ? 'pointer' : 'default' }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12, color: '#666' }}>
            <span>
              <span style={directionStyle(a)}>
                {directionLabel(a)}
              </span>
              {showStaff && <span> · {a.staff_name ? `${a.staff_name}'s mailbox` : 'Shared mailbox'}</span>}
            </span>
            <span>{whenLabel(a)}</span>
          </div>
          <div style={{ fontWeight: 600, fontSize: 14, marginTop: 4, overflowWrap: 'anywhere' }}>{a.topic || '(no subject)'}</div>
          <div style={{ fontSize: 13, color: '#333', marginTop: 2 }}>
            {a.commercial_entity?.name || '—'}
            {a.also_on_count > 0 && <span style={{ color: '#888' }}> · also on {a.also_on_count} other record{a.also_on_count === 1 ? '' : 's'}</span>}
          </div>
          <div style={{ fontSize: 12, color: '#888', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={a.direction === 'outbound' ? a.email_to : a.email_from}>
            {a.direction === 'outbound' ? `To ${a.email_to || '—'}` : `From ${a.email_from || '—'}`}
          </div>
          {a.can_remove && onRemoved && (
            <div style={{ marginTop: 6 }}>
              <RemoveControl
                activity={a}
                open={removingId === a.id}
                onOpen={() => setRemovingId(a.id)}
                onClose={() => setRemovingId(null)}
                onRemoved={onRemoved}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default SyncedEmailList;
